import { useState, useMemo } from 'react';
import { useLoader } from '../../../../context/LoaderContext';
import { useNavigate } from 'react-router-dom';
import { DISPATCH_COLUMN, POPUP_TYPE, POPUP_MSG, ROUTES } from '../../../../constants';
import {
  vendorCreateDispatch,
  markDispatched,
  printPackingList,
  dispatchBillCreation,
  vendorUpdateDispatchDetail,
} from '../../../../service';
import { popup } from '../../../../utils';

/**
 * useDispatchActions - Hook for managing dispatch API actions and related state
 *
 * This hook centralizes all dispatch-related API operations and provides a unified
 * interface for different vendor types (clothing/ESUN vs fabric/dyeing). It handles:
 * - Submitting dispatches (marking items as dispatched)
 * - Printing packing lists
 * - Creating dispatch bills
 * - Updating dispatch details (invoice/container numbers)
 * - Creating new dispatches (clothing vendor only)
 *
 * The hook supports two selection modes:
 * 1. "page" mode: User manually selects individual items (stored in poList)
 * 2. "all" mode: User selects all matching records (server-side filtering with exclusions)
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.selection - Selection state from useDispatchSelection hook
 *   Contains: poList, selectionMode, excludedIds, and mode setters
 * @param {Object} options.filters - Filter state from useDispatchFilters hook
 *   Contains: searchTerm, dispatchStatusFilter, stateFilter, billStatusFilter, buildSearchPayload, buildFilterPayload
 * @param {Object} options.groupByState - GroupBy state from useDispatchGroupBy hook
 *   Contains: groupBy, selectedItem, shouldApplyGroupBy, buildGroupByPayload
 * @param {Object} options.data - Data state from useDispatchData hook
 *   Contains: getDispatchList, setReload
 * @param {string} options.detailRoute - Route to navigate to for dispatch details
 *   Default: ROUTES.DISPATCH_DETAILS (clothing), can be ROUTES.FABRIC_DISPATCH_DETAIL
 * @param {string} options.postSubmitBehavior - Behavior after successful dispatch submission
 *   - 'bill': Keep bill mode active (default)
 *   - 'createBill': Transition to create bill mode (fabric/dyeing vendors)
 *   - 'clear': Clear selection entirely (clothing vendor)
 * @param {boolean} options.showContainerInput - Whether container input is shown
 *   Affects validation: clothing vendor requires invoice OR container, others require invoice
 * @param {Function} options.onSuccess - Optional callback invoked after any successful action
 *
 * @returns {Object} Hook return value containing:
 *   - Loading states for each action
 *   - Input states (invoiceNo, containerNo) with setters
 *   - Validation state (isUpdateDetailsDisabled)
 *   - Action handlers for each operation
 *   - Payload builders (exposed for testing)
 *   - Detail route for navigation
 */
export const useDispatchActions = ({
  selection,
  filters,
  groupByState,
  data,
  detailRoute = ROUTES.DISPATCH_DETAILS,
  postSubmitBehavior = 'bill', // 'bill' | 'createBill' | 'clear'
  showContainerInput = false,
  onSuccess,
} = {}) => {
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  // =====================================================================
  // LOADING STATES
  // Each action has its own loading state to allow UI to show spinners
  // and disable buttons independently for each operation
  // =====================================================================
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [isBillCreationLoading, setIsBillCreationLoading] = useState(false);
  const [isUpdateDetailsLoading, setIsUpdateDetailsLoading] = useState(false);
  const [isCreateDispatchLoading, setIsCreateDispatchLoading] = useState(false);

  // =====================================================================
  // INPUT STATES
  // Text inputs for updating dispatch details (invoice number, container number)
  // These are controlled inputs managed by this hook
  // =====================================================================
  const [invoiceNo, setInvoiceNo] = useState('');
  const [containerNo, setContainerNo] = useState('');

  // =====================================================================
  // DESTRUCTURE DEPENDENCIES
  // Extract needed values from selection and listState hooks
  // =====================================================================

  // From selection hook - manages which items are selected and selection mode
  const {
    poList, // Array of selected dispatch items (in "page" mode)
    setPoList, // Setter for poList
    selectionMode, // "page" (manual selection) or "all" (select all matching)
    excludedIds, // Set of IDs to exclude when in "all" mode
    setBillMode, // Toggle bill/group dispatch mode
    setCreateBillMode, // Toggle create bill mode
    setSelectionMode, // Switch between "page" and "all" modes
    setExcludedIds, // Update excluded IDs set
    clearSelection, // Reset all selection state
  } = selection || {};

  // From filters hook - manages search and filter state
  const {
    searchTerm, // Current search text
    dispatchStatusFilter, // Dispatch status dropdown value (e.g., "Draft", "Dispatched")
    stateFilter, // State dropdown value (e.g., "Assigned", "Done")
    billStatusFilter, // Bill status dropdown value (e.g., "Created", "Not Created")
  } = filters || {};

  // From groupByState hook - manages groupBy state
  const {
    groupBy, // Group by field name(s), comma-separated for multiple
    selectedItem, // Selected group by values
    shouldApplyGroupBy, // Function to check if groupBy should be applied
  } = groupByState || {};

  // From data hook - manages data fetching
  const {
    getDispatchList, // Function to refresh the dispatch list
    setReload, // Trigger a reload of the list
  } = data || {};

  // =====================================================================
  // PAYLOAD BUILDERS
  // These functions construct the request payloads for API calls.
  // =====================================================================

  /**
   * Builds the search portion of the API payload
   * When searchTerm is present, searches across multiple fields simultaneously
   * using OR logic (any field matching returns the record)
   *
   * @returns {Object} Search payload object with field:searchTerm pairs, or empty object
   */
  const buildSearchPayload = () => {
    return searchTerm
      ? {
          name: searchTerm, // Dispatch name/reference
          origin: searchTerm, // Origin document reference
          'move_ids.future_lot': searchTerm, // Lot number (nested field)
          invoice_no: searchTerm, // Invoice number
          'partner_id.name': searchTerm, // Partner/vendor name (nested field)
          partner_ref: searchTerm, // Partner reference
          finished_products: searchTerm, // Finished product names
          physical_container_no: searchTerm, // Container number
        }
      : {};
  };

  /**
   * Builds the filter portion of the API payload
   * Converts dropdown selections into API filter parameters
   *
   * The DISPATCH_COLUMN constants at indices 15, 16, 17 represent default values:
   * - [15]: Default bill status (e.g., "All")
   * - [16]: Default dispatch status (e.g., "All Status")
   * - [17]: Default state (e.g., "All State")
   *
   * Only includes filters when they differ from the default (user made a selection)
   *
   * @returns {Object} Filter payload with selection filters and bill status
   */
  const buildFilterPayload = () => {
    return {
      // Nested selection object for status filters
      selection: {
        // Include dispatch_status filter only if not default
        ...(dispatchStatusFilter !== DISPATCH_COLUMN[16].name
          ? { dispatch_status: dispatchStatusFilter.toLowerCase() }
          : {}),
        // Include state filter only if not default
        ...(stateFilter !== DISPATCH_COLUMN[17].name
          ? { state: stateFilter.toLowerCase() }
          : {}),
      },
      // Bill status filter at top level (API expects different structure)
      // Replace space with underscore for values like "Not Created" -> "not_created"
      ...(billStatusFilter !== DISPATCH_COLUMN[15].name
        ? { dispatch_bill_id: billStatusFilter.toLowerCase().replace(' ', '_') }
        : {}),
    };
  };

  /**
   * Builds the groupBy portion of the API payload
   * Supports single field or multiple comma-separated fields for grouping
   *
   * The groupby_specifics object maps field names to arrays of selected values,
   * allowing filtering to specific groups (e.g., specific partners or dates)
   *
   * Handles two formats for selectedItem values:
   * 1. Array format [id, name]: Extracts the ID
   * 2. Direct value: Uses as-is
   *
   * Converts string numbers to actual numbers for proper API handling
   *
   * @returns {Object} GroupBy payload with field name and specifics
   */
  const buildGroupByPayload = () => {
    const shouldUse = shouldApplyGroupBy?.();
    let groupbyParams = {
      groupby_field: shouldUse ? groupBy : '',
      groupby_specifics: {},
    };

    // Only build specifics if groupBy is active and values are selected
    if (shouldUse && selectedItem?.length) {
      // Handle multiple groupBy fields (comma-separated)
      if (groupBy?.includes(',')) {
        const fields = groupBy.split(',');
        const groupbySpecifics = {};

        // Apply same selected values to each field
        fields.forEach((field) => {
          groupbySpecifics[field.trim()] = selectedItem.map((item) => {
            // Handle [id, name] tuple format from dropdowns
            if (Array.isArray(item) && item.length > 1) {
              const id = item[0];
              return !isNaN(Number(id)) ? Number(id) : id;
            }
            // Handle direct value format
            return !isNaN(Number(item)) ? Number(item) : item;
          });
        });

        groupbyParams.groupby_specifics = groupbySpecifics;
      } else {
        // Single groupBy field
        groupbyParams.groupby_specifics = {
          [groupBy]: selectedItem.map((item) => {
            if (Array.isArray(item) && item.length > 1) {
              const id = item[0];
              return !isNaN(Number(id)) ? Number(id) : id;
            }
            return !isNaN(Number(item)) ? Number(item) : item;
          }),
        };
      }
    }

    return groupbyParams;
  };

  /**
   * Builds payload for "select all" mode operations
   * Used when user clicks "Select All" to operate on all matching records
   *
   * In this mode, the server applies the same search/sort/groupBy filters
   * and operates on all matching records EXCEPT those in excludedIds
   *
   * @param {Object} additionalFields - Extra fields to merge into payload
   * @returns {Object} Complete payload for select-all operations
   */
  const buildSelectAllPayload = (additionalFields = {}) => {
    return {
      select_all: true, // Tell server to select all matching
      dispatch_id: [], // Empty array since server finds IDs
      search: buildSearchPayload(), // Current search filters
      sort: buildFilterPayload(), // Current filter settings
      ...buildGroupByPayload(), // Current groupBy settings
      excluded_ids: Array.from(excludedIds || new Set()), // IDs user unchecked
      group_dispatch: true, // Flag for group dispatch operation
      ...additionalFields, // Allow caller to add more fields
    };
  };

  /**
   * Builds payload for "page" mode operations (manual selection)
   * Used when user manually checks individual items
   *
   * Simply extracts IDs from the poList array of selected items
   *
   * @param {Object} additionalFields - Extra fields to merge into payload
   * @returns {Object} Complete payload for page-selection operations
   */
  const buildPageSelectionPayload = (additionalFields = {}) => {
    const dispatch_id = poList?.map((item) => item.id) || [];
    return {
      select_all: false, // Tell server we're providing specific IDs
      dispatch_id, // Array of selected dispatch IDs
      group_dispatch: true, // Flag for group dispatch operation
      ...additionalFields, // Allow caller to add more fields
    };
  };

  // =====================================================================
  // ACTION HANDLERS
  // These functions perform API calls and handle the response.
  // Each follows a similar pattern:
  // 1. Build appropriate payload based on selection mode
  // 2. Set loading state
  // 3. Call API
  // 4. Handle success (refresh list, update state, show popup)
  // 5. Handle errors
  // 6. Clear loading state in finally block
  // =====================================================================

  /**
   * Handles submitting selected dispatches (marking them as dispatched)
   *
   * Validation: All selected items must have a container assigned
   * (no empty string, false, or 'N/A' values)
   *
   * Post-submit behavior varies by vendor type:
   * - 'createBill': Updates poList status and switches to create bill mode
   *   (fabric/dyeing vendors - they create bills after dispatching)
   * - 'clear': Clears the selection entirely
   *   (clothing vendor - bills are created separately)
   *
   * On success, refreshes the dispatch list and resets selection mode
   */
  const handleSubmitDispatch = async () => {
    try {
      // Validate: all selected items must have container assigned
      const containers = poList?.map((item) => item.container) || [];

      if (
        !containers.includes('') &&
        !containers.includes(false) &&
        !containers.includes('N/A')
      ) {
        // Build payload based on selection mode
        let payload;
        if (selectionMode === 'all') {
          payload = buildSelectAllPayload();
        } else {
          payload = buildPageSelectionPayload();
        }

        setIsSubmitLoading(true);
        const response = await markDispatched({ data: payload });

        if (response) {
          // Refresh the dispatch list to reflect status changes
          await getDispatchList?.();

          // Handle post-submit behavior based on vendor configuration
          if (postSubmitBehavior === 'createBill' && selectionMode === 'page') {
            // Fabric/dyeing vendor flow: transition to create bill mode
            // Update local poList to reflect new 'dispatched' status
            // This allows immediate bill creation without re-selecting
            setPoList?.((prevPoList) =>
              prevPoList.map((item) => ({
                ...item,
                dispatch_status: 'dispatched',
              })),
            );
            setBillMode?.(false);
            setCreateBillMode?.(true);
          } else if (postSubmitBehavior === 'clear') {
            // Clothing vendor flow: just clear the selection
            setPoList?.([]);
          }

          popup('success', response.message);

          // Reset selection state regardless of post-submit behavior
          setBillMode?.(false);
          setSelectionMode?.('page');
          setExcludedIds?.(new Set());

          onSuccess?.();
        }
      } else {
        // Validation failed: show error about missing container
        popup(POPUP_TYPE.ERR, POPUP_MSG.MISSING_INVOICE);
      }
    } catch (error) {
      setLoading(false);
      console.error('Server Error: ', error);
      popup(POPUP_TYPE.ERR, error.message || 'Something went wrong');
    } finally {
      setIsSubmitLoading(false);
    }
  };

  /**
   * Handles printing packing list for selected dispatches
   *
   * Opens the generated PDF in a new browser tab
   * Clears selection and exits print mode after successful print
   *
   * @param {Array} poListParam - Optional override for poList (for direct calls)
   */
  const handlePrintPacking = async (poListParam) => {
    try {
      setIsPrintLoading(true);
      let payload;

      if (selectionMode === 'all') {
        // Select all mode: let server determine which records to include
        payload = buildSelectAllPayload();
      } else {
        // Page mode: use provided poList or fall back to hook's poList
        const dispatch_id = (poListParam || poList)?.map((po) => po.id) || [];
        payload = {
          select_all: false,
          dispatch_id,
          group_dispatch: true,
        };
      }

      const response = await printPackingList({ data: payload });

      if (response?.data) {
        // Response contains URL to the generated PDF
        const downloadUrl = response.data;
        window.open(downloadUrl, '_blank');

        // Clean up: clear selection and exit print mode
        clearSelection?.();
        selection?.setPrintMode?.(false);

        onSuccess?.();
      } else {
        console.error('Server Error or Invalid Response: ', response);
      }
    } catch (error) {
      console.error('Server Error: ', error);
    } finally {
      setIsPrintLoading(false);
    }
  };

  /**
   * Handles creating a dispatch bill from selected dispatched items
   *
   * Only available for fabric/dyeing vendors (showCreateBill feature flag)
   * Items must be in 'dispatched' or 'received' status with no existing bill
   *
   * On success:
   * - Shows success popup
   * - Refreshes dispatch list
   * - Clears selection
   * - Navigates to the newly created bill detail page
   */
  const handleCreateDispatchBill = async () => {
    try {
      setIsBillCreationLoading(true);
      let payload;

      if (selectionMode === 'all') {
        payload = buildSelectAllPayload();
      } else {
        payload = buildPageSelectionPayload();
      }

      const response = await dispatchBillCreation({ data: payload });

      if (response) {
        const createdBills = response?.data?.records || [];

        if (createdBills.length > 0) {
          // Take the first created bill for navigation
          const billData = createdBills[0];

          popup(POPUP_TYPE.SUCCESS, response?.message || 'Bill(s) created successfully.');

          // Refresh list and clean up
          getDispatchList?.();
          clearSelection?.();
          setCreateBillMode?.(false);

          // Navigate to bill detail page after short delay
          // Delay ensures state updates complete before navigation
          // Bill name may contain slashes (e.g., "BILL/2024/001"), replace with underscores for URL
          setTimeout(() => {
            navigate(`${ROUTES.BILLS}/${billData.name.replaceAll('/', '_')}`, {
              state: billData,
            });
          }, 100);

          onSuccess?.();
          return;
        }
      }
    } catch (error) {
      console.error('Error while creating dispatch bill:', error);
    } finally {
      setIsBillCreationLoading(false);
    }
  };

  /**
   * Handles updating dispatch details (invoice number and/or container number)
   *
   * Updates the selected dispatches with new invoice_no and/or physical_container_no
   * Available in bill mode when items are selected
   *
   * Validation varies by vendor:
   * - Clothing vendor (showContainerInput=true): requires invoice OR container
   * - Other vendors: requires invoice number
   *
   * On success, clears input fields and triggers list reload
   */
  const handleUpdateDispatchDetail = async () => {
    try {
      let payload;

      if (selectionMode === 'all') {
        // Build select-all payload but remove group_dispatch flag
        // (update endpoint doesn't use this flag)
        payload = {
          ...buildSelectAllPayload(),
          group_dispatch: undefined,
        };
        delete payload.group_dispatch;
      } else {
        // Simple payload with just the selected IDs
        const dispatch_id = poList?.map((po) => po.id) || [];
        payload = {
          select_all: false,
          dispatch_id,
        };
      }

      // Add the fields being updated (only if they have values)
      if (invoiceNo) payload.invoice_no = invoiceNo;
      if (containerNo) payload.physical_container_no = containerNo;

      setIsUpdateDetailsLoading(true);

      const response = await vendorUpdateDispatchDetail({ data: payload });

      if (response) {
        popup(POPUP_TYPE.SUCCESS, response.message || 'Dispatch updated successfully');

        // Clear inputs after successful update
        setInvoiceNo('');
        setContainerNo('');

        // Trigger list refresh to show updated values
        setReload?.(true);

        onSuccess?.();
      }
    } catch (error) {
      popup(POPUP_TYPE.ERR, error.message || 'Something went wrong');
    } finally {
      setIsUpdateDetailsLoading(false);
    }
  };

  /**
   * Handles creating a new dispatch record (clothing vendor only)
   *
   * Creates an empty dispatch in the backend and navigates to the
   * create dispatch page where user can add items
   *
   * This is only available for clothing/ESUN vendors (showCreateDispatch feature flag)
   */
  const handleCreateDispatch = async () => {
    try {
      setIsCreateDispatchLoading(true);

      // Create empty dispatch record
      const response = await vendorCreateDispatch({ data: {} });

      if (response?.data?.records?.length > 0) {
        // Navigate to create dispatch page with the new record data
        navigate(ROUTES.CREATE_DISPATCH, { state: response.data.records[0] });
      }
    } catch (error) {
      setLoading(false);
      popup(POPUP_TYPE.ERR, error.message);
    } finally {
      setIsCreateDispatchLoading(false);
    }
  };

  // =====================================================================
  // VALIDATION
  // Computed validation states for enabling/disabling UI elements
  // =====================================================================

  /**
   * Determines if the Update Dispatch Detail button should be disabled
   *
   * Validation rules:
   * - Clothing vendor (showContainerInput=true): Need either invoiceNo OR containerNo
   * - Other vendors: Need invoiceNo
   * - Always disabled while update is in progress
   */
  const isUpdateDetailsDisabled = useMemo(() => {
    if (showContainerInput) {
      // Clothing vendor: either invoiceNo OR containerNo is required
      return (!invoiceNo && !containerNo) || isUpdateDetailsLoading;
    }
    // Other vendors: invoiceNo is required
    return !invoiceNo || isUpdateDetailsLoading;
  }, [invoiceNo, containerNo, isUpdateDetailsLoading, showContainerInput]);

  // =====================================================================
  // RETURN VALUE
  // Organized by category for easy consumption by components
  // =====================================================================
  return {
    // Loading states - use these to show spinners and disable buttons
    isSubmitLoading,
    isPrintLoading,
    isBillCreationLoading,
    isUpdateDetailsLoading,
    isCreateDispatchLoading,

    // Input states - controlled inputs for invoice/container fields
    invoiceNo,
    setInvoiceNo,
    containerNo,
    setContainerNo,

    // Validation - computed disabled states
    isUpdateDetailsDisabled,

    // Action handlers - call these from button onClick handlers
    handleSubmitDispatch,
    handlePrintPacking,
    handleCreateDispatchBill,
    handleUpdateDispatchDetail,
    handleCreateDispatch,

    // Payload builders - exposed for testing or custom usage
    // These are implementation details but useful for unit tests
    buildSearchPayload,
    buildFilterPayload,
    buildGroupByPayload,
    buildSelectAllPayload,
    buildPageSelectionPayload,

    // Detail route - pass to table for row click navigation
    detailRoute,
  };
};
